import { contacts } from '@/data/contacts';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import PageMeta from '@/shared/components/PageMeta';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import ExternalLink from '@/shared/ui/external-link';
import { Camera, Code, Cpu, Dog, Github, HeartPulse, Linkedin, Mail, Twitter } from 'lucide-react';

const contactButtons = [
  { label: 'GitHub', href: contacts.github, icon: Github },
  { label: 'LinkedIn', href: contacts.linkedin, icon: Linkedin },
  { label: 'X / Twitter', href: contacts.twitter, icon: Twitter },
  { label: 'Email', href: contacts.emailHref, icon: Mail }
];

export default function AboutPage() {
  const { language } = useLanguage();
  const zh = language === 'zh';

  const facets = [
    {
      icon: Code,
      color: 'text-blue-500 bg-blue-500/10',
      title: zh ? '写代码' : 'Code',
      body: zh
        ? 'React / TypeScript / Node.js 做 Web，Swift 做 iOS，Electron 做桌面端，数据库用 Postgres。'
        : 'React / TypeScript / Node.js for the web, Swift for iOS, Electron for desktop, Postgres for data.'
    },
    {
      icon: Cpu,
      color: 'text-orange-500 bg-orange-500/10',
      title: zh ? '造无人机' : 'Robotics & UAVs',
      body: zh
        ? '在 STM32H743、ESP32-S3、RK3588 上用 C/C++ 和 Python 开发，集成 GNSS、IMU、dTOF 激光雷达等传感器，下一步想上 Jetson 做边缘计算。'
        : 'I develop on STM32H743, ESP32-S3 and RK3588 with C/C++ and Python, integrating GNSS receivers, IMUs and dTOF LiDAR — aiming for the Nvidia Jetson series next for better edge computing.'
    },
    {
      icon: Camera,
      color: 'text-green-500 bg-green-500/10',
      title: zh ? '拍照片' : 'Photography',
      body: zh
        ? '记录伦敦的光线、旅途和日常瞬间，作品在这个空间的照片墙上。'
        : 'Chasing London light, travels and everyday moments — the results live on the portfolio wall here.'
    },
    {
      icon: HeartPulse,
      color: 'text-red-500 bg-red-500/10',
      title: zh ? '急救志愿' : 'First aid',
      body: zh
        ? '持 AHA 急救认证，随第一反应®参与马拉松赛道保障。'
        : 'AHA-licensed first-aider, running marathon track support with First Respond®.'
    },
    {
      icon: Dog,
      color: 'text-yellow-600 bg-yellow-500/10',
      title: zh ? '资助导盲犬' : 'Guide dogs',
      body: zh
        ? '正在资助 Henry、Norman、Clover 三只幼犬学员完成两年的导盲犬训练。'
        : 'Sponsoring three puppy trainees — Henry, Norman and Clover — through their two-year guide dog training journey.'
    }
  ];

  return (
    <div className="pt-24 px-6">
      <PageMeta title="About" description="Kaibin Zhang beyond the CV — code, robotics, photography and volunteering." path="/space/about" />
      <div className="max-w-3xl mx-auto space-y-14">
        <header className="space-y-4">
          <h1 className="text-4xl font-bold">{zh ? '关于我' : 'About me'}</h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {zh
              ? '我是张铠彬（Kaibin Zhang），在 UCL 读历史、政治与经济学。研究兴趣落在社会科学、认知与行为科学、哲学与意义诠释的交叉处——理解社会系统中的人如何做决定。'
              : 'I’m Kaibin Zhang, reading History, Politics and Economics at UCL. My interests sit at the intersection of social science, cognitive and behavioural science, and philosophy — understanding how people decide within social systems.'}
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {zh
              ? '课堂之外，我是一个全栈开发者，也做硬件。这一页是简历放不下的部分。'
              : 'Outside the curriculum I’m a full-stack developer who also builds hardware. This page is for the parts a CV can’t hold.'}
          </p>
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {facets.map(facet => (
            <Card key={facet.title} className="border-0 bg-card/50 backdrop-blur-sm">
              <CardContent className="p-5">
                <div className={`inline-flex p-2 rounded-lg mb-3 ${facet.color}`}>
                  <facet.icon className="w-5 h-5" />
                </div>
                <h2 className="font-semibold mb-1.5">{facet.title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{facet.body}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="text-center space-y-6 pb-8">
          <h2 className="text-2xl font-bold">{zh ? '保持联系' : 'Stay in touch'}</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {contactButtons.map(contact => (
              <Button key={contact.label} variant="outline" asChild>
                <ExternalLink href={contact.href} className="inline-flex items-center">
                  <contact.icon className="w-4 h-4 mr-2" />
                  {contact.label}
                </ExternalLink>
              </Button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
